import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2, AlertTriangle, ArrowRight, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function CoachInviteAccept() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { acceptCoachInvite } = useAuth();
    const hasProcessed = useRef(false);
    const [error, setError] = useState(null);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (hasProcessed.current) return;
        hasProcessed.current = true;
        const token = params.get("token");
        if (!token) {
            setError("Missing or invalid invite link.");
            return;
        }
        (async () => {
            try {
                await acceptCoachInvite(token);
                setDone(true);
                setTimeout(() => navigate("/coach", { replace: true }), 1200);
            } catch (e) {
                setError(e?.response?.data?.detail || "This invite is invalid or has expired.");
            }
        })();
    }, [params, acceptCoachInvite, navigate]);

    return (
        <div
            data-testid="coach-invite-accept-page"
            className="min-h-screen bg-brand-obsidian text-white flex flex-col items-center justify-center px-6"
        >
            {error ? (
                <div data-testid="coach-invite-accept-error" className="max-w-md w-full text-center border border-zinc-900 bg-zinc-950 p-10">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-brand-red/10 border border-brand-red/40 text-brand-red mb-5">
                        <AlertTriangle className="w-6 h-6" />
                    </div>
                    <h1 className="font-display text-3xl tracking-wider text-white mb-3 uppercase">Invite not valid</h1>
                    <p className="text-zinc-200 text-sm leading-relaxed">{error}</p>
                    <Link to="/" className="btn-tactical mt-7 inline-flex">
                        Back to site <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            ) : done ? (
                <div data-testid="coach-invite-accept-success" className="max-w-md w-full text-center border border-zinc-900 bg-zinc-950 p-10">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-brand-red text-white mb-5">
                        <Check className="w-6 h-6" />
                    </div>
                    <h1 className="font-display text-3xl tracking-wider text-white mb-3 uppercase">Welcome, coach.</h1>
                    <p className="text-zinc-200 text-sm leading-relaxed">Taking you to the coach console…</p>
                </div>
            ) : (
                <>
                    <Loader2 className="w-8 h-8 text-brand-red animate-spin" />
                    <div className="mt-5 text-[10px] tracking-[0.3em] uppercase text-zinc-300">Accepting invitation</div>
                </>
            )}
        </div>
    );
}
